import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const lapTimeVariants = cva(
  "font-mono font-bold relative inline-block",
  {
    variants: {
      variant: {
        primary: "text-2xl text-gray-900 drop-shadow-sm",
        secondary: "text-lg text-gray-700",
        small: "text-sm text-gray-600",
        highlight: "text-2xl text-orange-600 drop-shadow-md",
        personal_best: "text-lg text-emerald-600 font-extrabold",
      },
      styleVariant: {
        default: "",
        racing: "tracking-wider text-shadow-md bg-gradient-to-b from-gray-900 to-gray-700 bg-clip-text text-transparent",
        digital: "bg-gray-900 text-green-400 px-2 py-1 rounded border font-mono tracking-widest shadow-inner",
      }
    },
    defaultVariants: {
      variant: "primary",
      styleVariant: "default",
    },
  }
);

interface LapTimeProps extends Omit<React.ComponentProps<"span">, "style">, VariantProps<typeof lapTimeVariants> {
  timeMs: number;
  showMilliseconds?: boolean;
  isPersonalBest?: boolean;
}

function formatLapTime(ms: number, showMilliseconds: boolean = true): string {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const milliseconds = ms % 1000;

  if (showMilliseconds) {
    return `${minutes}:${seconds.toString().padStart(2, "0")}.${milliseconds
      .toString()
      .padStart(3, "0")}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function LapTime({
  className,
  variant,
  styleVariant,
  timeMs,
  showMilliseconds = true,
  isPersonalBest = false,
  ...props
}: LapTimeProps) {
  const computedVariant = isPersonalBest ? "personal_best" : variant;

  return (
    <span
      className={cn(lapTimeVariants({ variant: computedVariant, styleVariant }), className)}
      {...props}
    >
      {formatLapTime(timeMs, showMilliseconds)}
    </span>
  );
}

export { LapTime, lapTimeVariants, formatLapTime };